// src/hooks/useCityHighlights.ts
//
// Subscribes to /users/{uid}/city_highlights/{cityKey} for the
// user's CURRENT city. We resolve the city from the user doc's
// locationContext block — that's where the curator's location
// pipeline writes lastCountry / lastCity — and rebuild the key the
// same way the server does (normalize → snake_case).
//
// Returns null while the city is unknown OR while generation is in
// flight; the row component renders a skeleton in those states.
//
// IMPORTANT: a user can have many city_highlights docs (one per
// city they've visited). This hook only watches the active one, so
// the row never shows stale info from a previous trip.

import { useEffect, useRef, useState } from 'react';
import { doc, onSnapshot, Timestamp, getDoc } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { CityHighlights, CityHighlightItem } from '../types/collections';

const tsToDate = (v: any): Date | null => {
  if (!v) return null;
  if (v instanceof Timestamp) return v.toDate();
  if (typeof v?.toDate === 'function') return v.toDate();
  return null;
};

/** MUST mirror server-side cityKey() in cityHighlights.ts. The
 *  server uses normalizeName(country) + "__" + normalizeName(city)
 *  with spaces collapsed to underscores. */
function cityKey(country: string, city: string): string {
  const norm = (s: string) =>
    s
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  return (norm(country) + '__' + norm(city)).replace(/\s+/g, '_');
}

interface UseCityHighlightsReturn {
  highlights: CityHighlights | null;
  isLoading: boolean;
  city: string | null;
  country: string | null;
  /** Discovered count / total — convenience for the row header. */
  discoveredCount: number;
  totalCount: number;
}

export const useCityHighlights = (): UseCityHighlightsReturn => {
  const [highlights, setHighlights] = useState<CityHighlights | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [city, setCity] = useState<string | null>(null);
  const [country, setCountry] = useState<string | null>(null);
  const unsubRef = useRef<(() => void) | null>(null);
  const userDocUnsubRef = useRef<(() => void) | null>(null);

  // Phase 1: subscribe to /users/{uid} to track current city.
  // Phase 2: when city changes, swap the inner listener to the new
  // highlight doc.
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setIsLoading(false);
      return;
    }
    userDocUnsubRef.current?.();
    const userRef = doc(db, 'users', user.uid);
    const unsub = onSnapshot(
      userRef,
      (snap) => {
        const d = snap.data() as any;
        const lc = d?.locationContext || {};
        const nextCountry: string | null =
          lc.lastCountry || lc.country || null;
        const nextCity: string | null = lc.lastCity || lc.city || null;
        setCountry(nextCountry);
        setCity(nextCity);
      },
      (err) => {
        console.warn('user doc listener for highlights:', err);
        setIsLoading(false);
      }
    );
    userDocUnsubRef.current = unsub;
    return () => userDocUnsubRef.current?.();
  }, []);

  // Phase 2: per-city highlights doc listener.
  useEffect(() => {
    const user = auth.currentUser;
    if (!user || !country || !city) {
      setHighlights(null);
      // Important: only stop "loading" once we know there's nothing
      // to load. Without a city, we're not really loading.
      setIsLoading(false);
      return;
    }
    unsubRef.current?.();
    const key = cityKey(country, city);
    const ref = doc(db, 'users', user.uid, 'city_highlights', key);

    // Initial fast read so the row paints something even before
    // the listener delivers the first snapshot.
    setIsLoading(true);
    getDoc(ref)
      .then((snap) => {
        if (!snap.exists()) {
          setHighlights(null);
        }
      })
      .catch(() => {});

    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setHighlights(null);
          setIsLoading(false);
          return;
        }
        const d = snap.data() as any;
        const items: CityHighlightItem[] = Array.isArray(d.items)
          ? d.items.map((it: any, idx: number) => ({
              id: it.id || `hl_${idx}`,
              title: it.title || '',
              ai_hint: it.ai_hint || '',
              aliases: Array.isArray(it.aliases) ? it.aliases : [],
              category: it.category || 'icon',
              rationale: it.rationale || '',
              kind: it.kind || 'other',
              location:
                it.location?.latitude !== undefined
                  ? {
                      latitude: it.location.latitude,
                      longitude: it.location.longitude,
                    }
                  : it.location?._latitude !== undefined
                  ? {
                      latitude: it.location._latitude,
                      longitude: it.location._longitude,
                    }
                  : null,
              order_hint: it.order_hint ?? idx,
              cover_image_url: it.cover_image_url,
              cover_image_thumb: it.cover_image_thumb,
              discovered_at: tsToDate(it.discovered_at),
              discovered_via: it.discovered_via || null,
              discovered_visit_id: it.discovered_visit_id || null,
            }))
          : [];
        setHighlights({
          id: snap.id,
          city: d.city,
          country: d.country,
          city_key: d.city_key,
          generated_at: tsToDate(d.generated_at),
          status: d.status || 'ready',
          basis: d.basis || 'cold_start',
          items,
          dismissed_item_ids: Array.isArray(d.dismissed_item_ids)
            ? d.dismissed_item_ids
            : [],
        });
        setIsLoading(false);
      },
      (err) => {
        console.warn('city_highlights listener err:', err);
        setIsLoading(false);
      }
    );
    unsubRef.current = unsub;
    return () => unsubRef.current?.();
  }, [country, city]);

  const total = highlights?.items.length ?? 0;
  const discovered =
    highlights?.items.filter((it) => !!it.discovered_at).length ?? 0;

  return {
    highlights,
    isLoading,
    city,
    country,
    discoveredCount: discovered,
    totalCount: total,
  };
};
